import type { Paginated } from "@/@types/paginated";
import { Tags } from "@/enum/tags";
import type {
  RecipientSortColumn,
  SectorEnum,
  ShippingTypeEnum,
  SortDirection,
} from "@/pages/conciliation/sector-emails/constants";
import {
  blobDownloadResponseHandler,
  BlobDownload,
  EmptyResponse,
} from "@/utils/blob-response-handler";
import { toQueryParams } from "@/utils/to-query-params";
import { apiSlice } from "./http";

export type ShippingRecipientStatus = "ACTIVE" | "INACTIVE";

export interface ShippingRecipientResponse {
  id: number;
  sector: SectorEnum;
  name: string;
  email: string;
  shipping_types: ShippingTypeEnum[];
  status: ShippingRecipientStatus;
  version: number;
}

export type RecipientsPageResponse = Paginated<ShippingRecipientResponse> & {
  meta: { total_unfiltered: number };
};

export interface ShippingRecipientListParams {
  search?: string;
  sectors?: SectorEnum[];
  shipping_types?: ShippingTypeEnum[];
  active?: boolean[];
  page: number;
  perPage: number;
  sort?: RecipientSortColumn;
  direction?: SortDirection;
}

export interface ShippingRecipientHistoryEntry {
  id: number;
  changed_at: string;
  changed_by: string;
  description: string;
}

export interface ShippingRecipientCreateBody {
  sector: SectorEnum;
  name: string;
  email: string;
  shipping_types: ShippingTypeEnum[];
}

export interface ShippingRecipientUpdateBody
  extends Partial<ShippingRecipientCreateBody> {
  version: number;
  status?: ShippingRecipientStatus;
}

export interface UpdateShippingRecipientArgs {
  id: number;
  body: ShippingRecipientUpdateBody;
}

export interface DeleteShippingRecipientArgs {
  id: number;
  version: number;
}

const RESOURCE_URL: string = "/shipping-recipient";

export const shippingRecipientApi = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    getShippingRecipients: builder.query<
      RecipientsPageResponse,
      ShippingRecipientListParams
    >({
      query: (filters) => ({
        url: `${RESOURCE_URL}?${toQueryParams(filters)}`,
        method: "GET",
      }),
      providesTags: [Tags.SHIPPING_RECIPIENT],
    }),

    exportShippingRecipients: builder.query<
      BlobDownload | EmptyResponse,
      ShippingRecipientListParams
    >({
      query: (filters) => ({
        url: `${RESOURCE_URL}/export?${toQueryParams(filters)}`,
        method: "GET",
        responseHandler: (response: Response) =>
          blobDownloadResponseHandler<EmptyResponse>(response),
      }),
    }),

    getShippingRecipientHistory: builder.query<
      ShippingRecipientHistoryEntry[],
      number
    >({
      query: (id) => ({
        url: `${RESOURCE_URL}/${id}/history`,
        method: "GET",
      }),
      providesTags: [Tags.SHIPPING_RECIPIENT_HISTORY],
    }),

    createShippingRecipient: builder.mutation<
      ShippingRecipientResponse,
      ShippingRecipientCreateBody
    >({
      query: (body) => ({
        url: RESOURCE_URL,
        method: "POST",
        body,
      }),
      invalidatesTags: [Tags.SHIPPING_RECIPIENT, Tags.SHIPPING_RECIPIENT_HISTORY],
    }),

    updateShippingRecipient: builder.mutation<
      ShippingRecipientResponse,
      UpdateShippingRecipientArgs
    >({
      query: ({ id, body }) => ({
        url: `${RESOURCE_URL}/${id}`,
        method: "PUT",
        body,
      }),
      invalidatesTags: [Tags.SHIPPING_RECIPIENT, Tags.SHIPPING_RECIPIENT_HISTORY],
    }),

    deleteShippingRecipient: builder.mutation<void, DeleteShippingRecipientArgs>(
      {
        query: ({ id, version }) => ({
          url: `${RESOURCE_URL}/${id}?version=${version}`,
          method: "DELETE",
        }),
        invalidatesTags: [Tags.SHIPPING_RECIPIENT, Tags.SHIPPING_RECIPIENT_HISTORY],
      }
    ),
  }),
});

export const {
  useGetShippingRecipientsQuery,
  useLazyExportShippingRecipientsQuery,
  useGetShippingRecipientHistoryQuery,
  useCreateShippingRecipientMutation,
  useUpdateShippingRecipientMutation,
  useDeleteShippingRecipientMutation,
} = shippingRecipientApi;
